/*
  facets.js: filters, sort, pagination and load-more for collection and search pages.
  Without JS everything still works as a GET form and plain links. With JS:
  - changes re-render the section through the Section Rendering API (?section_id=…) and update the URL (pushState);
  - back/forward re-render from the URL; focus moves to the results heading after an update;
  - below 1024px the filter rail is a drawer / bottom sheet with "Show N results".
*/
const { openPanel, closePanel, safeGet, safeSet } = window.theme;

const VIEW_KEY = 'listing-view';

class FacetForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.bind();
    this.applyView(safeGet(localStorage, VIEW_KEY) || 'grid');
    this.maybeDefaultInStock();
    this.watchInfinite();
    if (!window.__facetPop) {
      window.__facetPop = true;
      window.addEventListener('popstate', () => {
        const ff = document.querySelector('facet-form');
        if (ff) ff.render(location.href, { push: false, focus: false });
      });
    }
  }

  get form() { return this.querySelector('[data-facet-form]'); }

  bind() {
    let timer;
    this.addEventListener('change', (e) => {
      const t = e.target;
      if (t.matches('[data-filter-search]')) return;
      if (t.matches('[data-range-min], [data-range-max]')) return;
      if (!t.form && !t.matches('[data-sort]')) return;
      clearTimeout(timer);
      const delay = t.type === 'number' ? 500 : 0;
      timer = setTimeout(() => this.submit(), delay);
    });

    this.addEventListener('input', (e) => {
      const t = e.target;
      if (t.matches('[data-filter-search]')) {
        const q = t.value.trim().toLowerCase();
        const list = t.closest('.c-filter__body').querySelector('[data-filter-list]');
        list.classList.toggle('is-expanded', !!q);
        list.querySelectorAll('[data-label]').forEach((li) => li.classList.toggle('is-hidden', !!q && !li.dataset.label.includes(q)));
      }
    });

    this.addEventListener('click', (e) => {
      const more = e.target.closest('[data-filter-more]');
      if (more) {
        const list = more.closest('.c-filter__body').querySelector('[data-filter-list]');
        const open = list.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', open);
        return;
      }
      const link = e.target.closest('[data-facet-link], [data-pagination] a');
      if (link) {
        e.preventDefault();
        this.render(link.href, { scroll: !!link.closest('[data-pagination]') });
        return;
      }
      const loadMore = e.target.closest('[data-load-more]');
      if (loadMore) { e.preventDefault(); this.loadMore(loadMore); return; }
      const view = e.target.closest('[data-view]');
      if (view) { this.applyView(view.dataset.view); safeSet(localStorage, VIEW_KEY, view.dataset.view); return; }
      if (e.target.closest('[data-filters-open]')) {
        openPanel(this.querySelector('[data-filters]'), e.target.closest('[data-filters-open]'));
        return;
      }
      if (e.target.closest('[data-filters-close]')) {
        closePanel(this.querySelector('[data-filters]'));
      }
    });
  }

  buildUrl() {
    const fd = new FormData(this.form);
    const params = new URLSearchParams();
    for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
    return `${this.form.getAttribute('action') || this.dataset.baseUrl}?${params.toString()}`;
  }

  submit() { this.render(this.buildUrl()); }

  async render(url, { push = true, focus = true, scroll = false } = {}) {
    const results = this.querySelector('[data-results]');
    const filters = this.querySelector('[data-filters]');
    const drawerOpen = filters?.classList.contains('is-open');
    const openState = {};
    this.querySelectorAll('details.c-filter').forEach((d) => { openState[d.id] = d.open; });
    const expanded = [...this.querySelectorAll('[data-filter-list].is-expanded')].map((l) => l.closest('details')?.id);
    const bodyScroll = this.querySelector('[data-filter-groups]')?.scrollTop || 0;
    const active = document.activeElement;
    const activeKey = active && this.contains(active) && active.name ? `${active.name}=${active.value}` : null;

    this.classList.add('is-loading');
    results?.setAttribute('aria-busy', 'true');
    try {
      const u = new URL(url, location.origin);
      u.searchParams.set('section_id', this.sectionId);
      const res = await fetch(u);
      if (!res.ok) throw new Error(res.statusText);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('facet-form');
      if (!fresh) throw new Error('No listing in response');

      this.innerHTML = fresh.innerHTML;
      const newFilters = this.querySelector('[data-filters]');
      if (drawerOpen && newFilters) {
        newFilters.classList.add('is-open');
        // keep theme.js drawer bookkeeping pointing at the new element
        openPanel(newFilters);
      }
      this.querySelectorAll('details.c-filter').forEach((d) => { if (d.id in openState) d.open = openState[d.id]; });
      expanded.forEach((id) => id && document.getElementById(id)?.querySelector('[data-filter-list]')?.classList.add('is-expanded'));
      const groups = this.querySelector('[data-filter-groups]');
      if (groups) groups.scrollTop = bodyScroll;
      this.applyView(safeGet(localStorage, VIEW_KEY) || 'grid');

      const clean = new URL(url, location.origin);
      clean.searchParams.delete('section_id');
      if (push) history.pushState({ facets: true }, '', clean.toString());

      if (drawerOpen && activeKey) {
        const [name, value] = activeKey.split('=');
        [...this.querySelectorAll(`[name="${CSS.escape(name)}"]`)].find((el) => el.value === value)?.focus({ preventScroll: true });
      } else if (focus) {
        const heading = this.querySelector('[id^="ResultsHeading"]');
        heading?.focus({ preventScroll: !scroll });
      }
      if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.watchInfinite();
      document.dispatchEvent(new CustomEvent('theme:section:load', { detail: { id: this.sectionId, target: this } }));
    } catch (err) {
      location.href = url; // fall back to a normal page load
    } finally {
      this.classList.remove('is-loading');
      this.querySelector('[data-results]')?.removeAttribute('aria-busy');
    }
  }

  /* infinite scroll: the Load more button loads by itself when it nears the viewport, up to data-infinite times */
  watchInfinite() {
    this.infiniteIO?.disconnect();
    const btn = this.querySelector('[data-load-more][data-infinite]');
    if (!btn || !('IntersectionObserver' in window)) return;
    this.autoLoads = 0;
    const max = parseInt(btn.dataset.infinite, 10) || 4;
    this.infiniteIO = new IntersectionObserver((entries) => {
      if (!entries.some((en) => en.isIntersecting) || btn.classList.contains('is-loading')) return;
      if (this.autoLoads >= max) { this.infiniteIO.disconnect(); return; }
      this.autoLoads += 1;
      this.loadMore(btn, { auto: true });
    }, { rootMargin: '0px 0px 600px 0px' });
    this.infiniteIO.observe(btn);
  }

  async loadMore(btn, { auto = false } = {}) {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    try {
      const u = new URL(btn.href, location.origin);
      u.searchParams.set('section_id', this.sectionId);
      const doc = new DOMParser().parseFromString(await (await fetch(u)).text(), 'text/html');
      const items = doc.querySelectorAll('[data-grid] > li');
      const grid = this.querySelector('[data-grid]');
      const firstNew = items[0];
      items.forEach((li) => grid.appendChild(document.importNode(li, true)));
      const nextBtn = doc.querySelector('[data-load-more]');
      if (nextBtn) btn.href = nextBtn.href; else btn.closest('.c-listing__more')?.remove();
      const count = doc.querySelector('.c-toolbar__count');
      if (count) this.querySelector('.c-toolbar__count')?.replaceWith(document.importNode(count, true));
      // keyboard users who pressed the button land on the first new product; auto-loads leave focus alone
      if (firstNew && !auto) grid.querySelector(`li:nth-child(${grid.children.length - items.length + 1}) a`)?.focus({ preventScroll: true });
    } finally {
      btn.classList.remove('is-loading');
    }
    // still in view after a short page (few products added): observe again so it keeps going
    if (auto && btn.isConnected && this.infiniteIO) { this.infiniteIO.unobserve(btn); this.infiniteIO.observe(btn); }
  }

  applyView(view) {
    const grid = this.querySelector('[data-grid]');
    if (!grid || !this.querySelector('[data-view]')) return;
    grid.classList.toggle('is-list', view === 'list');
    this.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.view === view));
  }

  maybeDefaultInStock() {
    const section = this.closest('[data-default-in-stock]');
    if (section?.dataset.defaultInStock !== 'true') return;
    if (location.search.includes('filter.v.availability')) return;
    if (safeGet(sessionStorage, `instock-${location.pathname}`)) return; // customer cleared it this session
    const box = this.querySelector('input[name="filter.v.availability"][value="1"]');
    if (!box) return;
    safeSet(sessionStorage, `instock-${location.pathname}`, '1');
    box.checked = true;
    const url = this.buildUrl();
    this.render(url, { push: false, focus: false }).then(() => history.replaceState({}, '', url));
  }
}
if (!customElements.get('facet-form')) customElements.define('facet-form', FacetForm);

/* dual price slider synced with the two number fields */
class PriceRange extends HTMLElement {
  connectedCallback() {
    this.min = this.querySelector('[data-min]');
    this.max = this.querySelector('[data-max]');
    this.rMin = this.querySelector('[data-range-min]');
    this.rMax = this.querySelector('[data-range-max]');
    this.fill = this.querySelector('[data-fill]');
    const top = Number(this.dataset.max) || 100;
    const paint = () => {
      const a = Math.min(Number(this.rMin.value), Number(this.rMax.value));
      const b = Math.max(Number(this.rMin.value), Number(this.rMax.value));
      this.fill.style.left = `${(a / top) * 100}%`;
      this.fill.style.right = `${100 - (b / top) * 100}%`;
    };
    let t;
    const fromSlider = () => {
      if (Number(this.rMin.value) > Number(this.rMax.value)) [this.rMin.value, this.rMax.value] = [this.rMax.value, this.rMin.value];
      this.min.value = Number(this.rMin.value) > 0 ? this.rMin.value : '';
      this.max.value = Number(this.rMax.value) < top ? this.rMax.value : '';
      paint();
      clearTimeout(t);
      t = setTimeout(() => this.min.dispatchEvent(new Event('change', { bubbles: true })), 450);
    };
    this.rMin.addEventListener('input', fromSlider);
    this.rMax.addEventListener('input', fromSlider);
    const fromFields = () => {
      this.rMin.value = this.min.value || 0;
      this.rMax.value = this.max.value || top;
      paint();
    };
    this.min.addEventListener('input', fromFields);
    this.max.addEventListener('input', fromFields);
    paint();
  }
}
if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);
